import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { FileText, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { StatusBadge, fmtDate, btnPrimary, btnSecondary } from "@/components/common/Form";
import { EmptyState, LoadingState, ErrorState } from "@/components/common/States";

export function RfqItems({ rfq }) {
  return (
    <ul className="divide-y divide-border rounded-lg border border-border" data-testid="rfq-items">
      {rfq.items.map((i) => (
        <li key={i.id} className="flex items-center gap-3 p-3 text-sm" data-testid={`rfq-item-${i.sku}`}>
          {i.image && <img src={i.image} alt="" className="h-12 w-12 rounded-md object-cover bg-muted" />}
          <div className="flex-1 min-w-0">
            <div className="font-medium text-stone-900 line-clamp-1">{i.product_name_snapshot}{i.variant_name_snapshot && ` · ${i.variant_name_snapshot}`}</div>
            <div className="text-xs text-stone-500">SKU {i.sku} · MOQ {i.moq}</div>
          </div>
          <div className="text-right"><div className="font-semibold text-stone-900">{i.quantity} units</div>{i.requested_unit_price != null && <div className="text-xs text-stone-500">listed {formatINR(i.requested_unit_price)}/unit</div>}</div>
        </li>
      ))}
    </ul>
  );
}

export function QuoteCard({ quote }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 text-sm" data-testid={`rfq-quote-${quote.id}`}>
      <div className="flex items-center justify-between mb-2"><span className="font-semibold text-stone-900">Vendor quotation</span><StatusBadge value={quote.status} /></div>
      <div className="grid grid-cols-2 gap-2 text-stone-600">
        <div>Unit price<div className="font-bold text-secondary text-base" data-testid="quote-unit-price">{formatINR(quote.quoted_unit_price)}</div></div>
        <div>Quantity<div className="font-semibold text-stone-900">{quote.quoted_quantity} units</div></div>
        <div>Total<div className="font-semibold text-stone-900">{formatINR(quote.quoted_total)}</div></div>
        <div>Valid until<div className="font-semibold text-stone-900">{fmtDate(quote.validity_date)}</div></div>
      </div>
      {quote.note && <p className="mt-2 text-stone-600 whitespace-pre-line">{quote.note}</p>}
    </div>
  );
}

export function RfqMeta({ rfq, vendorView }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 text-sm text-stone-600 space-y-1" data-testid="rfq-meta">
      {!vendorView && <div>Vendor: <Link to={`/store/${rfq.vendor_slug}`} className="font-semibold text-secondary">{rfq.vendor_name}</Link></div>}
      {vendorView && <div>Buyer: <b className="text-stone-900">{rfq.business_name || "Business buyer"}</b></div>}
      <div>Delivery: <b className="text-stone-900">{rfq.delivery_city}, {rfq.delivery_state} {rfq.delivery_pin}</b></div>
      <div>Submitted: {fmtDate(rfq.created_at)}</div>
      {rfq.note && <div className="pt-1">Note: <span className="text-stone-800 whitespace-pre-line">{rfq.note}</span></div>}
      {rfq.rejection_reason && <div>Rejection reason: <span className="text-red-700">{rfq.rejection_reason}</span></div>}
    </div>
  );
}

export function BusinessRfqs() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let c = false;
    api.get("/business/rfqs").then((r) => { if (!c) setRows(r.data); }).catch(() => { if (!c) setRows([]); });
    return () => { c = true; };
  }, []);
  if (rows === null) return <LoadingState />;
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="business-rfqs-page">
      <h1 className="text-3xl font-serif font-bold text-stone-900 mb-6">Wholesale Quote Requests</h1>
      {rows.length === 0 ? <EmptyState title="No RFQs yet" message="Request a wholesale quote from any product marked Wholesale Available." action={<Link to="/b2b/products" className={btnPrimary}>Browse wholesale</Link>} /> : (
        <ul className="space-y-3" data-testid="business-rfq-list">
          {rows.map((r) => (
            <li key={r.id}><Link to={`/business/rfqs/${r.id}`} data-testid={`rfq-row-${r.rfq_number}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-border bg-card p-4 hover:border-primary/50">
              <div className="h-11 w-11 rounded-full bg-accent grid place-items-center text-secondary"><FileText size={20} /></div>
              <div className="flex-1 min-w-[180px]"><div className="font-semibold text-stone-900">{r.rfq_number}</div><div className="text-xs text-stone-500">{fmtDate(r.created_at)} · {r.vendor_name} · {r.item_count} units</div></div>
              <StatusBadge value={r.status} />
            </Link></li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function BusinessRfqDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [rfq, setRfq] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/business/rfqs/${id}`).then((r) => setRfq(r.data)).catch((e) => setError(formatApiError(e.response?.data?.detail))), [id]);
  useEffect(() => { load(); }, [load]);
  const act = async (action) => {
    const reason = action === "reject" ? window.prompt("Reason for rejecting (optional)") : null;
    if (action === "reject" && reason === null) return;
    setBusy(true);
    try { const r = await api.post(`/business/rfqs/${id}/${action}`, action === "reject" ? { reason } : {}); setRfq(r.data); toast.success(`Quotation ${action}ed`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (error) return <ErrorState message={error} />;
  if (!rfq) return <LoadingState />;
  const active = rfq.quotes.find((q) => q.status === "ACTIVE") || rfq.quotes[0];
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="business-rfq-detail">
      <Link to="/business/rfqs" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-secondary mb-4"><ArrowLeft size={14} /> All RFQs</Link>
      {params.get("new") && <div className="mb-4 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-800 text-sm p-3 inline-flex items-center gap-2" data-testid="rfq-confirmation"><CheckCircle2 size={16} /> RFQ submitted to {rfq.vendor_name}. You will be notified when a quotation arrives.</div>}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-serif font-bold text-stone-900" data-testid="rfq-number">{rfq.rfq_number}</h1>
        <StatusBadge value={rfq.status} testId="rfq-status" />
      </div>
      <div className="grid lg:grid-cols-[1fr_340px] gap-6">
        <div className="space-y-4">
          <RfqItems rfq={rfq} />
          {active ? <QuoteCard quote={active} /> : <p className="text-sm text-stone-500" data-testid="rfq-awaiting">Awaiting vendor quotation.</p>}
          {rfq.status === "QUOTED" && (
            <div className="flex gap-2">
              <button disabled={busy} onClick={() => act("accept")} data-testid="rfq-accept" className={btnPrimary}>Accept quotation</button>
              <button disabled={busy} onClick={() => act("reject")} data-testid="rfq-reject" className={btnSecondary}>Reject</button>
            </div>
          )}
          {rfq.status === "ACCEPTED" && <div className="rounded-lg bg-amber-50 border border-amber-100 text-amber-800 text-xs p-3" data-testid="rfq-payment-note">Quotation accepted. Payment status: <b>PENDING</b> — order & payment conversion will be available in a later phase.</div>}
        </div>
        <RfqMeta rfq={rfq} />
      </div>
    </div>
  );
}
